import React, { useState, useEffect } from 'react';
import { DoorOpen, Calendar, Clock, ArrowRight, Users, ExternalLink, X, Trash2 } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';

const SalasReservaWidget = () => {
    const [reserva, setReserva] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const navigate = useNavigate();

    // Check dismiss state
    useEffect(() => {
        const today = new Date().toDateString();
        const key = `widget_dismiss_salas_${today}`;
        if (localStorage.getItem(key)) setDismissed(true);
    }, []);

    useEffect(() => {
        fetchMiReserva();
    }, []);

    const fetchMiReserva = async () => {
        try {
            const response = await axiosInstance.get('/api/salas/reservas/mis-proximas');
            console.log('[SalasWidget DEBUG]', JSON.stringify(response.data, null, 2));
            if (response.data.success && response.data.reserva) {
                setReserva(response.data.reserva);
            }
        } catch (error) {
            console.log('Salas API error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = () => {
        if (window.confirm('¿Deseas ocultar este widget por hoy?')) {
            const today = new Date().toDateString();
            localStorage.setItem(`widget_dismiss_salas_${today}`, 'true');
            setDismissed(true);
        }
    };

    const handleCancel = async () => {
        if (!reserva) return;
        const salaName = reserva.sala?.nombre || 'Sala';
        const fecha = reserva.fecha ? new Date(reserva.fecha + 'T00:00:00').toLocaleDateString('es-VE', { day: 'numeric', month: 'short' }) : '';
        if (!window.confirm(`¿Estás seguro de cancelar tu reserva en ${salaName} para el ${fecha}?`)) return;

        setCancelling(true);
        try {
            await axiosInstance.put(`/api/salas/reservas/${reserva.id}/cancelar`);
            setReserva(null); // Widget will hide
        } catch (error) {
            console.error('Error cancelling reservation:', error);
            alert(error.response?.data?.error || 'Error al cancelar la reserva');
        } finally {
            setCancelling(false);
        }
    };

    // Generar URL de Google Calendar
    const generateGoogleCalendarUrl = () => {
        if (!reserva) return '#';
        const fecha = reserva.fecha;
        const horaInicio = reserva.hora_inicio;
        const horaFin = reserva.hora_fin;
        const startDt = `${fecha.replace(/-/g, '')}T${horaInicio.replace(':', '')}00`;
        const endDt = `${fecha.replace(/-/g, '')}T${horaFin.replace(':', '')}00`;
        const title = encodeURIComponent(
            reserva.motivo
                ? `${reserva.motivo} - ${reserva.sala?.nombre || 'Sala'}`
                : `Reunión - ${reserva.sala?.nombre || 'Sala'}`
        );
        const location = encodeURIComponent(
            `${reserva.sala?.nombre || 'Sala'}, Sede ${reserva.sala?.sede || ''}, SURA de Venezuela`
        );
        const details = encodeURIComponent(
            `Reserva de sala en Centro SURA\nSala: ${reserva.sala?.nombre || ''} (Sede ${reserva.sala?.sede || ''})\n` +
            (reserva.motivo ? `Motivo: ${reserva.motivo}` : '')
        );
        return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDt}/${endDt}&details=${details}&location=${location}&ctz=America/Caracas`;
    };

    // IGNORING DISMISSED FOR TESTING: If the user clicked 'X' earlier today, it would hide the widget.
    if (loading || !reserva) return null;

    // Auto-hide: if reservation date is in the past
    // Usamos T23:59:59 para que no lo oculte el mismo día a menos que pase la medianoche
    const reservaDate = reserva.fecha ? new Date(reserva.fecha + 'T23:59:59') : null;
    if (reservaDate && reservaDate < new Date()) return null;

    // Estado de la reserva
    const estadoConfig = {
        pendiente: {
            label: 'Pendiente',
            bg: 'from-amber-500 to-orange-600',
            bgLight: 'bg-amber-50',
            text: 'text-amber-600',
            border: 'border-amber-200',
            icon: '⏳'
        },
        aprobada: {
            label: 'Confirmada',
            bg: 'from-emerald-500 to-teal-600',
            bgLight: 'bg-emerald-50',
            text: 'text-emerald-600',
            border: 'border-emerald-200',
            icon: '✓'
        },
        rechazada: {
            label: 'Rechazada',
            bg: 'from-red-500 to-rose-600',
            bgLight: 'bg-red-50',
            text: 'text-red-600',
            border: 'border-red-200',
            icon: '✗'
        }
    };

    const config = estadoConfig[reserva.estado] || estadoConfig.pendiente;

    // Formatear fecha
    const fecha = reserva.fecha ? new Date(reserva.fecha + 'T00:00:00') : null;
    const fechaFormateada = fecha
        ? fecha.toLocaleDateString('es-VE', { weekday: 'short', day: 'numeric', month: 'short' })
        : '';

    return (
        <section className={`relative rounded-2xl overflow-hidden border ${config.border} shadow-sm group`}>
            {/* Dismiss button */}
            <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 z-20 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm text-gray-400 hover:text-gray-600 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                title="Ocultar por hoy"
            >
                <X size={14} />
            </button>

            {/* Gradient header */}
            <div className={`bg-gradient-to-r ${config.bg} px-5 py-4 text-white`}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <DoorOpen size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-lg">Reserva de Sala</h3>
                        <p className="text-white/80 text-sm flex items-center gap-1">
                            {config.icon} {config.label}
                        </p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className={`${config.bgLight} p-5`}>
                {/* Sala */}
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Sala</p>
                            <p className="text-xl font-bold text-gray-900 mt-1">
                                {reserva.sala?.nombre || 'Sala'}
                            </p>
                            {reserva.sala?.sede && (
                                <p className="text-sm text-gray-500">Sede {reserva.sala.sede}</p>
                            )}
                        </div>
                        <div className={`w-12 h-12 rounded-full ${config.bgLight} flex items-center justify-center`}>
                            <Users size={24} className={config.text} />
                        </div>
                    </div>
                </div>

                {/* Fecha y hora */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${config.bgLight}`}>
                        <Calendar size={16} className={config.text} />
                        <div>
                            <p className="text-xs text-gray-500">Fecha</p>
                            <p className="font-bold text-gray-900 capitalize">{fechaFormateada}</p>
                        </div>
                    </div>
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${config.bgLight}`}>
                        <Clock size={16} className={config.text} />
                        <div>
                            <p className="text-xs text-gray-500">Hora</p>
                            <p className="font-bold text-gray-900">
                                {reserva.hora_inicio} - {reserva.hora_fin}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Motivo */}
                {reserva.motivo && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {reserva.motivo}
                    </p>
                )}

                <a
                    href={generateGoogleCalendarUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-2.5 rounded-xl bg-white border-2 border-gray-200 text-gray-700 font-medium flex items-center justify-center gap-2 hover:bg-blue-50 hover:border-blue-200 transition-all group/cal mb-2"
                >
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <span className="text-sm">Agendar en Calendar</span>
                    <ExternalLink size={12} className="text-gray-400 group-hover/cal:text-blue-500 transition-colors" />
                </a>

                <button
                    onClick={() => navigate('/reserva-salas')}
                    className="w-full py-3 rounded-xl bg-gray-900 text-white font-medium flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors mb-2"
                >
                    Ver reservas
                    <ArrowRight size={16} />
                </button>

                {/* Cancel reservation button */}
                {(reserva.estado === 'pendiente' || reserva.estado === 'aprobada') && (
                    <button
                        onClick={handleCancel}
                        disabled={cancelling}
                        className="w-full py-2.5 rounded-xl border-2 border-red-200 text-red-600 font-medium flex items-center justify-center gap-2 hover:bg-red-50 hover:border-red-300 transition-all text-sm disabled:opacity-50"
                    >
                        <Trash2 size={14} />
                        {cancelling ? 'Cancelando...' : 'Cancelar reserva'}
                    </button>
                )}
            </div>
        </section>
    );
};

export default SalasReservaWidget;
